import { useEffect } from 'react'

import {
  ConnectionState,
  Participant,
  RemoteParticipant,
  RemoteTrackPublication,
  Room,
  RoomEvent,
} from 'livekit-client'
import { ParticipantPermission } from '@livekit/protocol'

import {
  readRecordingTarget,
  shouldIncludeParticipantInTargetRecording,
} from '@/src/lib/recordingTarget'
import { attributesAffectSubscriptions } from './subscriptionAttributes'

// ID для участников в основной комнате (без группы)
const MAIN_ROOM_GROUP_ID = 'main_room'

// Очищает и проверяет groupId участника
// Убирает пробелы и проверяет на пустые/невалидные значения
const normalizeGroupId = (participant?: Participant | RemoteParticipant | null) => {
  const rawGroupId = participant?.attributes?.groupId
  if (typeof rawGroupId !== 'string') {
    return null
  }

  const trimmed = rawGroupId.trim()
  // Проверяем на пустые строки и строки "null"/"undefined"
  if (trimmed.length === 0 || trimmed === 'null' || trimmed === 'undefined') {
    return null
  }

  return trimmed
}

// Получает groupId участника из metadata или атрибутов
// Сначала проверяет participantGroupMap в metadata (актуальные данные),
// затем fallback на атрибуты участника
const getEffectiveGroupId = (
  participant: Participant | RemoteParticipant | null | undefined,
  room?: Room,
  metadata?: { participantGroupMap?: Record<string, string | null>; groups?: unknown },
) => {
  if (!participant) return MAIN_ROOM_GROUP_ID

  // Используем переданный metadata или получаем из комнаты
  const roomMetadata =
    metadata ||
    (room?.metadata
      ? (() => {
          try {
            return JSON.parse(room.metadata)
          } catch {
            return {}
          }
        })()
      : {})

  // Если групп в комнате нет (группы завершены или ещё не созданы),
  // считаем, что все участники находятся в одном общем зале.
  const groupsArray = Array.isArray((roomMetadata as any).groups)
    ? ((roomMetadata as any).groups as unknown[])
    : []
  if (groupsArray.length === 0) {
    return MAIN_ROOM_GROUP_ID
  }

  const participantGroupMap = (roomMetadata as any).participantGroupMap as
    | Record<string, string | null>
    | undefined

  // Если участник есть в mapping - используем его groupId
  if (participantGroupMap && participant.identity in participantGroupMap) {
    const groupId = participantGroupMap[participant.identity]
    // null означает основную комнату (без группы)
    if (groupId === null || groupId === undefined || groupId === '') {
      return MAIN_ROOM_GROUP_ID
    }
    return groupId
  }

  // Fallback: используем атрибуты участника (если нет в metadata)
  return normalizeGroupId(participant) ?? MAIN_ROOM_GROUP_ID
}

// Управляет подписками на треки участников по группам
export const useGroupTrackSubscriptions = (room?: Room) => {
  useEffect(() => {
    if (!room) return

    const localParticipant = room.localParticipant
    
    // Кеш для метаданных комнаты (чтобы не парсить JSON на каждый вызов)
    let cachedMetadata: { participantGroupMap?: Record<string, string | null> } | null = null
    let cachedMetadataString = ''

    // Получает метаданные комнаты с кешированием
    const getRoomMetadata = () => {
      const metadataString = room.metadata || ''
      // Если metadata не изменилась - возвращаем кеш
      if (metadataString === cachedMetadataString && cachedMetadata) {
        return cachedMetadata
      }
      // Парсим и кешируем
      try {
        cachedMetadataString = metadataString
        cachedMetadata = metadataString ? JSON.parse(metadataString) : {}
        return cachedMetadata
      } catch (error) {
        console.error('[getRoomMetadata] Error parsing metadata:', error)
        return {}
      }
    }

    // Проверяет права на подписку
    const canLocalSubscribe = () => !!localParticipant?.permissions?.canSubscribe

    // Определяет, нужно ли подписываться на треки участника.
    //
    // Новая логика:
    // - Все участники (включая админов и кураторов) слышат и видят только тех,
    //   кто находится с ними в одной группе.
    // - Участники без группы считаются в основном зале (MAIN_ROOM_GROUP_ID)
    //   и слышат только тех, кто тоже в основном зале.
    // - Скрытые участники и рекордеры всегда игнорируются.
    // - Агенты: подписываемся на их треки (чтобы слышать их аудио),
    //   но визуально они скрыты через UI-фильтрацию.
    const shouldSubscribeToParticipant = (
      participant: RemoteParticipant,
      metadata?: { participantGroupMap?: Record<string, string | null> },
    ) => {
      // Проверяем права на подписку
      if (!canLocalSubscribe()) return false

      const recordingTarget = readRecordingTarget(localParticipant.attributes)
      if (
        localParticipant.permissions?.recorder &&
        recordingTarget.recordingCandidateKey
      ) {
        return shouldIncludeParticipantInTargetRecording(
          participant,
          recordingTarget,
          metadata?.participantGroupMap,
        )
      }

      // Для агентов: ВСЕГДА подписываемся на их треки (даже если hidden на старте)
      if (participant.permissions?.agent) {
        return true
      }

      // Пропускаем скрытых участников и рекордеров (но НЕ агентов — они уже обработаны выше)
      if (
        participant.permissions?.hidden ||
        participant.permissions?.recorder
      ) {
        return false
      }

      // Слышим и видим только участников из той же группы
      const participantGroupId = getEffectiveGroupId(participant, room, metadata)
      const localGroupId = getEffectiveGroupId(localParticipant, room, metadata)
      return participantGroupId === localGroupId
    }

    // Обновляет подписку на конкретный трек участника
    // Подписывается или отписывается в зависимости от групповой принадлежности
    const updatePublicationSubscription = (
      publication: RemoteTrackPublication,
      participant: RemoteParticipant,
      metadata?: { participantGroupMap?: Record<string, string | null> },
    ) => {
      const subscribe = shouldSubscribeToParticipant(participant, metadata)

      // Если статус подписки уже правильный - ничего не делаем
      if (publication.isSubscribed === subscribe) return

      try {
        publication.setSubscribed(subscribe)
      } catch (error) {
        console.error('Failed to update track subscription', {
          participantIdentity: participant.identity,
          trackSid: publication.trackSid,
          error,
        })
      }
    }

    // Обновляет подписки на все треки участника
    // Проходит по всем публикациям треков и обновляет подписки
    const updateParticipantSubscriptions = (
      participant: RemoteParticipant,
      metadata?: { participantGroupMap?: Record<string, string | null> },
    ) => {
      // Обновляем подписки на все треки участника
      participant.getTrackPublications().forEach((publication) => {
        // Обновляем только удаленные треки (не локальные)
        if (publication instanceof RemoteTrackPublication) {
          updatePublicationSubscription(publication, participant, metadata)
        }
      })
    }

    // Отписывается от всех треков всех удаленных участников
    // Используется при изменении группы локального участника для полной пересинхронизации
    const unsubscribeFromAll = () => {
      room.remoteParticipants.forEach((participant) => {
        participant.getTrackPublications().forEach((publication) => {
          // Отписываемся только от активных подписок на удаленные треки
          if (publication instanceof RemoteTrackPublication && publication.isSubscribed) {
            try {
              publication.setSubscribed(false)
            } catch (error) {
              console.error('Failed to unsubscribe from track', {
                participantIdentity: participant.identity,
                trackSid: publication.trackSid,
                error,
              })
            }
          }
        })
      })
    }

    // Синхронизирует подписки на все треки всех удаленных участников
    // Проходит по всем участникам и обновляет подписки согласно текущей группе
    const syncAllSubscriptions = () => {
      // Получаем metadata один раз для всех участников (оптимизация)
      const metadata = getRoomMetadata()
      room.remoteParticipants.forEach((participant) => {
        updateParticipantSubscriptions(participant, metadata)
      })
    }

    // Обработчик подключения к комнате
    // При подключении синхронизируем все подписки
    const handleRoomConnected = () => {
      syncAllSubscriptions()
    }

    // Обработчик подключения нового участника
    // Когда новый участник подключается, проверяем его группу и подписываемся на его треки
    const handleParticipantConnected = (participant: RemoteParticipant) => {
      updateParticipantSubscriptions(participant)
    }

    // Обработчик публикации нового трека
    // Когда участник публикует новый трек (камера, микрофон, скриншар),
    // проверяем его группу и подписываемся, если нужно
    const handleTrackPublished = (
      publication: RemoteTrackPublication,
      participant: RemoteParticipant,
    ) => {
      updatePublicationSubscription(publication, participant)
    }

    // Обработчик изменения атрибутов участника
    const handleParticipantAttributesChanged = (
      changedAttributes: Record<string, string>,
      participant: Participant,
    ) => {
      // Подписки зависят только от groupId (+ таргета записи): игнорируем handRaised и др.,
      // иначе любое изменение атрибутов дёргает полный resync всех треков (мигание видео).
      if (!attributesAffectSubscriptions(changedAttributes)) return

      // Если изменилась группа локального участника - переподписываемся на всех
      if (participant.isLocal) {
        unsubscribeFromAll()
        syncAllSubscriptions()
        return
      }

      // Если изменилась группа удаленного участника - обновляем подписки на его треки
      updateParticipantSubscriptions(participant as RemoteParticipant)
    }

    // Обработчик изменения прав участника
    const handleParticipantPermissionsChanged = (
      _prevPermissions: ParticipantPermission | undefined,
      participant: Participant,
    ) => {
      if (participant.isLocal) {
        unsubscribeFromAll()
        syncAllSubscriptions()
      } else {
        // Обновляем подписки при изменении прав remote-участника
        // (агент стартует hidden: true, затем меняет на hidden: false)
        updateParticipantSubscriptions(participant as RemoteParticipant)
      }
    }

    // Обработчик изменения метаданных комнаты
    // Вызывается когда изменяются метаданные (создание/удаление групп, перемещение участника)
    const handleRoomMetadataChanged = (metadataString: string) => {
      const timeForUpdate = 100
      
      // Сохраняем старый кеш ДО сброса
      const previousMetadata = cachedMetadata
      
      // Сбрасываем кеш метаданных
      cachedMetadataString = ''
      cachedMetadata = null
      
      try {
        const parsedMetadata = JSON.parse(metadataString)
        
        // Проверяем информацию о перемещении участника между группами
        if (parsedMetadata.lastParticipantGroupChange) {
          const { participantIdentity } = parsedMetadata.lastParticipantGroupChange
          
          // Находим перемещенного участника среди удаленных участников
          const movedParticipant = Array.from(room.remoteParticipants.values()).find(
            (p) => p.identity === participantIdentity,
          )
          
          if (movedParticipant) {
            // Задержка для обновления атрибутов участника в LiveKit
            // Затем обновляем подписки ТОЛЬКО для этого участника
            setTimeout(() => {
              updateParticipantSubscriptions(movedParticipant, parsedMetadata)
            }, timeForUpdate)
            return
          }
        }

        // Проверяем, завершились ли группы (удален массив groups)
        const hasGroups = Array.isArray(parsedMetadata.groups) && parsedMetadata.groups.length > 0
        const hadGroups = previousMetadata && Array.isArray((previousMetadata as any).groups) && (previousMetadata as any).groups.length > 0
        
        // Если группы были удалены (завершены), добавляем задержку для синхронизации
        // чтобы дать серверу время обновить атрибуты всех участников
        if (hadGroups && !hasGroups) {
          // Одна синхронизация с задержкой для учета обновления атрибутов
          setTimeout(() => {
            syncAllSubscriptions()
          }, timeForUpdate * 5)
          
          return
        }
      } catch (error) {
        console.error('[useGroupTrackSubscriptions] Error parsing room metadata:', error)
      }
      
      // Полная синхронизация только если создаются/удаляются группы или другие изменения
      syncAllSubscriptions()
    }

    // Подписываемся на события комнаты
    room.on(RoomEvent.Connected, handleRoomConnected)
    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected)
    room.on(RoomEvent.TrackPublished, handleTrackPublished)
    room.on(RoomEvent.ParticipantAttributesChanged, handleParticipantAttributesChanged)
    room.on(RoomEvent.ParticipantPermissionsChanged, handleParticipantPermissionsChanged)
    room.on(RoomEvent.RoomMetadataChanged, handleRoomMetadataChanged)

    // Если комната уже подключена - сразу синхронизируем подписки
    if (room.state === ConnectionState.Connected) {
      syncAllSubscriptions()
    }

    // Отписываемся от событий при размонтировании
    return () => {
      room.off(RoomEvent.Connected, handleRoomConnected)
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected)
      room.off(RoomEvent.TrackPublished, handleTrackPublished)
      room.off(RoomEvent.ParticipantAttributesChanged, handleParticipantAttributesChanged)
      room.off(RoomEvent.ParticipantPermissionsChanged, handleParticipantPermissionsChanged)
      room.off(RoomEvent.RoomMetadataChanged, handleRoomMetadataChanged)
    }
  }, [room])
}

